import { Struct } from '@bufbuild/protobuf';
import { ArtifactTypes } from '@mono/proto-clients';
import { artifactClient } from './connect/clients';
/**
 * Upload a file via tesoro: open a session, PUT bytes directly to MinIO with
 * the presigned URL, then mark complete and (auto-)kick off processing.
 *
 * Replaces the old POST `/api/v2/upload` (express → shanks) path. Bytes go
 * client → MinIO directly, so neither express nor sabaody buffer them.
 */ export async function uploadAttachmentViaTesoro(file, options) {
    // 1. Reserve an artifact row + presigned PUT URL.
    const session = await artifactClient.createUploadSession({
        tenantId: options.tenantId,
        ownerUserId: options.ownerUserId,
        chatId: options.chatId ?? '',
        projectId: options.projectId ?? '',
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: BigInt(file.size),
        sourceKind: ArtifactTypes.ArtifactSourceKind.ATTACHMENT,
        fileCategory: ArtifactTypes.ArtifactFileCategory.SOURCE_DOCUMENT
    });
    if (!session.uploadUrl) {
        throw new Error('Tesoro returned no upload URL');
    }
    // 2. PUT to MinIO. XHR (not fetch) — fetch can't surface upload progress
    //    so we'd have to fake the spinner; this preserves the real signal.
    await new Promise((resolve, reject)=>{
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', session.uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        if (options.onProgress) {
            xhr.upload.onprogress = (e)=>{
                if (e.lengthComputable) {
                    options.onProgress?.(Math.round(e.loaded / e.total * 100));
                }
            };
        }
        xhr.onload = ()=>{
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`MinIO PUT failed: ${xhr.status} ${xhr.statusText}`));
            }
        };
        xhr.onerror = ()=>reject(new Error('MinIO PUT failed: network error'));
        xhr.send(file);
    });
    // 3. Stamp the row 'uploaded' and (auto)start reverie. Reverie reads these
    //    keys as strings (see services/reverie/pkg/pipeline/etl.go) — keep them
    //    string-typed for compatibility with the existing job runner.
    const processingOptions = Struct.fromJson({
        use_deep_processing: options.useDeepProcessing ? 'true' : 'false',
        output_format: options.outputFormat ?? 'markdown'
    });
    const completed = await artifactClient.completeUpload({
        uploadId: session.uploadId,
        autoProcess: options.autoProcess ?? true,
        processingOptions
    });
    if (!completed.artifact) {
        throw new Error('Tesoro returned no artifact after completeUpload');
    }
    return {
        artifactId: completed.artifact.artifactId,
        filename: completed.artifact.filename,
        mimeType: completed.artifact.mimeType,
        jobId: completed.processingJobId || undefined
    };
}
/**
 * Turn an already-uploaded ZIP artifact into one child artifact per usable
 * member via tesoro's ExpandArchiveArtifact. The archive artifact itself
 * stays in storage — only its children come back, since only they carry
 * extracted text a chat request could actually cite.
 */ export async function expandArchiveArtifactViaTesoro(artifactId, options) {
    const processingOptions = Struct.fromJson({
        use_deep_processing: options.useDeepProcessing ? 'true' : 'false',
        output_format: options.outputFormat ?? 'markdown'
    });
    const resp = await artifactClient.expandArchiveArtifact({
        artifactId,
        autoProcess: options.autoProcess,
        processingOptions
    });
    return {
        children: resp.children.map((child)=>({
                artifactId: child.artifactId,
                filename: child.filename,
                mimeType: child.mimeType,
                sizeBytes: Number(child.sizeBytes),
                checksumSha256: child.checksumSha256,
                processingJobId: child.processingJobId || undefined
            })),
        skipped: resp.skipped.map((member)=>({
                path: member.path,
                reason: member.reason
            }))
    };
}
